import React, { useState, useRef, useEffect, useCallback, useMemo, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import MapSettingsButton from './MapSettingsButton';
import { useIntl } from 'react-intl';
import Slider from 'Components/Slider';
import { showLandmarksOnMap, showGeofencesOnMap, showPreventOverlapLabels, showUnitLabels, showUnitCluster, normalizeUnitLabelSelect, isWebGLAvailable } from '../../../../../../../components/Map/utils';
import { HasPermission } from "../../../../../../../core/security";
import { validateJsonObject } from "../../../../../../../core/data/Helpers";

import { getLandmarksAndGeofences as getKmls, loadObjectsLandmarksOnMap, loadObjectsGeofenceOnMap } from "Redux/actions";
import { getPreferences, savePreference } from "Redux/actions";
import { convertIntFromObjectToBoolean, convertStringToIntegerBoolean } from 'Core/data/Helpers';

import './style.scss';

const SLIDER_MIN = 4;
const SLIDER_MAX = 22;
const DEFAULT_SLIDE_VALUE = 10;
const DEBOUNCE_MS = 1000;

const SliderRealtimeMaps = memo(({ value, onChange }) => (
    <Slider
        min={SLIDER_MIN}
        max={SLIDER_MAX}
        sliderOneColor={false}
        showValue
        valueLabelDisplay="off"
        value={value}
        onChange={onChange}
    />
));
SliderRealtimeMaps.displayName = 'SliderRealtimeMaps';

const MapSettingPreference = ({ mapRef }) => {

    const { messages, locale } = useIntl();
    const dispatch = useDispatch();
    const timerRef = useRef(null);

    const [zoom, setZoom] = useState(4);
    const [mapSettingsVars, setMapSettingsVars] = useState({});
    const [showVehicleTrailsState, setShowVehicleTrailsState] = useState(false);
    const [preferences, setPreferences] = useState(null);
    const [preferencesLoaded, setPreferencesLoaded] = useState(false);

    const preferencesUser = useSelector(state => state.preferencesRedux.preferencesUser);
    const returnServer = useSelector(state => state.preferencesRedux.returnServer);
    const authenticatedId = useSelector(state => state.securityRedux.authenticatedId);
    const showVehicleTrails = useSelector(state => state.realtimeMapUtilitiesvehiclesTrailRedux.showVehicleTrails);
    const loadedJsonFiles = useSelector(state => state.catalogsRedux.loadedJsonFiles);
    const user = useSelector(state => state.securityRedux.user);

    const isSpanish = locale === 'es';

    // Only expose the Hardware Acceleration toggle when the browser actually
    // supports WebGL.  On unsupported browsers we silently fall back to the
    // legacy renderer in googleProvider, so the toggle would be a no-op.
    const webglSupported = useMemo(() => isWebGLAvailable(), []);

    // Cleanup timer on unmount
    useEffect(() => () => {
        if (timerRef.current) clearTimeout(timerRef.current);
    }, []);

    const updateKmlsToShow = useCallback((settings = null) => {
        const s = settings || mapSettingsVars;
        showLandmarksOnMap(mapRef, loadObjectsLandmarksOnMap, s, DEFAULT_SLIDE_VALUE);
        showGeofencesOnMap(s, DEFAULT_SLIDE_VALUE, mapRef, loadObjectsGeofenceOnMap);
    }, [mapRef, mapSettingsVars]);

    const savePreferencesData = useCallback((settingsVars) => {
        if (!authenticatedId) return;
        let preferencesToSave = { ...preferences, ...settingsVars };

        if (preferencesToSave.hasOwnProperty('labelSelect_unit') || preferencesToSave.hasOwnProperty('labelSelect_driver')) {
            delete preferencesToSave.unitLabelSelect;
        }

        preferencesToSave = convertIntFromObjectToBoolean(preferencesToSave);
        if (preferencesToSave) {
            setPreferences(preferencesToSave);
            dispatch(savePreference({ entityName: "User", entityId: authenticatedId, preferenceKey: "mapSettings", preferenceValue: preferencesToSave }));
        }
    }, [authenticatedId, preferences, dispatch]);

    const onOptionChange = useCallback((key, checked, isBoolean = true) => {
        const tempSettings = { ...mapSettingsVars, [key]: isBoolean ? Boolean(checked) : checked };

        if (key === 'labelSelect_unit' || key === 'labelSelect_driver') {
            const hasAnyLabel = tempSettings['labelSelect_unit'] || tempSettings['labelSelect_driver'];
            tempSettings['unitLabelShow'] = Boolean(hasAnyLabel);
            if (!hasAnyLabel) {
                tempSettings['unitLabelShowOverlap'] = false;
            }
        }

        setMapSettingsVars(tempSettings);
        savePreferencesData(tempSettings);

        if (key === 'geofenceShow' || key === 'landmarkShow') {
            updateKmlsToShow(tempSettings);
        }
        if (key === 'unitCluster') {
            showUnitCluster(tempSettings, mapRef);
        }
        if (key === 'unitLabelShow' || key === 'labelSelect_unit' || key === 'labelSelect_driver' || key === 'darkLabels') {
            const unitLabelSelectForMap = {
                label: Boolean(tempSettings['labelSelect_unit'] ?? false),
                driver: Boolean(tempSettings['labelSelect_driver'] ?? false)
            };
            showUnitLabels({ ...tempSettings, unitLabelSelect: unitLabelSelectForMap }, mapRef);
        }
        if (key === 'unitLabelShowOverlap') {
            showPreventOverlapLabels(tempSettings, mapRef);
        }
        // Toggling Hardware Acceleration changes whether the map is created
        // with `mapId` (vector / WebGL) or without (legacy raster).  The
        // option cannot be changed on an existing google.maps.Map instance,
        // and the React-rendered control buttons are moved into Google's
        // controls container on first init, so a full reload is the
        // simplest reliable path.
        if (key === 'hardwareAcceleration') {
            // Give the savePreference dispatch a moment to flush before reload.
            setTimeout(() => window.location.reload(), 250);
        }
    }, [mapSettingsVars, mapRef, savePreferencesData, updateKmlsToShow]);

    const onOptionSliderChange = useCallback((key, value) => {
        const tempSettings = { ...mapSettingsVars, [key]: value };
        setMapSettingsVars(tempSettings);

        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            timerRef.current = null;
            savePreferencesData(tempSettings);
            updateKmlsToShow(tempSettings);
        }, DEBOUNCE_MS);
    }, [mapSettingsVars, savePreferencesData, updateKmlsToShow]);

    const handleLandmarkMarkersSlider = useCallback((event) => {
        onOptionSliderChange('zoomLandmarkMarkersVisible', event);
    }, [onOptionSliderChange]);

    const handleLandmarkRadiusSlider = useCallback((event) => {
        onOptionSliderChange('zoomLandmarkRadiusVisibleZoom', event);
    }, [onOptionSliderChange]);

    const handleGeofenceSlider = useCallback((event) => {
        onOptionSliderChange('zoomGeofenceVisible', event);
    }, [onOptionSliderChange]);

    useEffect(() => {
        if (showVehicleTrailsState && !showVehicleTrails && mapSettingsVars?.['unitCluster']) {
            const showLabel = mapSettingsVars['unitLabelShow'];
            if (showLabel) mapRef.current.addLabelMarkers();
            mapRef.current.showMarkerCluster(mapSettingsVars['unitCluster'], false, showLabel);
        }
        setShowVehicleTrailsState(showVehicleTrails);
    }, [showVehicleTrails]);

    useEffect(() => {
        if (loadedJsonFiles && !loadedJsonFiles.length > 0) dispatch(getKmls());
        if (authenticatedId) {
            dispatch(getPreferences({ entityName: "User", entityId: authenticatedId, force: true }));
        }
        if (mapRef) {
            setZoom(mapRef?.current?.getCurrentZoom());
            mapRef.current.onSuscribeZoom((paramZoom) => {
                setZoom(paramZoom);
            });
        }
    }, []);

    useEffect(() => {
        if (preferencesUser) {
            const preferencesKeyTemp = preferencesUser?.find(x => x.preferenceKey === "mapSettings");
            let preferencesJSON = preferencesKeyTemp?.preferenceValue !== "" && preferencesKeyTemp?.preferenceValue || "{}";
            preferencesJSON = validateJsonObject(preferencesJSON);
            loadPreferencesDefault(preferencesJSON);
            setPreferences(preferencesJSON);
        }
    }, [returnServer]);

    const loadPreferencesDefault = (prefs) => {
        if (!prefs) return;

        const mapSettingsPreferences = {};
        const hasNewProperties = prefs.hasOwnProperty('labelSelect_unit') || prefs.hasOwnProperty('labelSelect_driver');

        for (const key of Object.keys(prefs)) {
            let value = prefs[key];

            if (key === 'unitLabelSelect' && !hasNewProperties) {
                const normalized = normalizeUnitLabelSelect(value);
                mapSettingsPreferences['labelSelect_unit'] = normalized.label;
                mapSettingsPreferences['labelSelect_driver'] = normalized.driver;
            } else if (key === 'unitLabelSelect' && hasNewProperties) {
                continue;
            } else {
                value = convertStringToIntegerBoolean(value);
                if (typeof value !== "boolean" && !isNaN(value) && value < 4) {
                    mapSettingsPreferences[key] = Boolean(value);
                } else if (typeof value === 'string') {
                    mapSettingsPreferences[key] = value;
                } else {
                    mapSettingsPreferences[key] = isNaN(value) ? false : value;
                }
            }
        }

        if (mapSettingsPreferences['labelSelect_unit'] === undefined) {
            mapSettingsPreferences['labelSelect_unit'] = false;
        }
        if (mapSettingsPreferences['labelSelect_driver'] === undefined) {
            mapSettingsPreferences['labelSelect_driver'] = false;
        }

        mapSettingsPreferences['unitLabelShow'] = Boolean(
            mapSettingsPreferences['labelSelect_unit'] || mapSettingsPreferences['labelSelect_driver']
        );

        const unitLabelSelectForMap = {
            label: Boolean(mapSettingsPreferences['labelSelect_unit'] ?? false),
            driver: Boolean(mapSettingsPreferences['labelSelect_driver'] ?? false)
        };

        showUnitCluster(mapSettingsPreferences, mapRef);
        setMapSettingsVars(mapSettingsPreferences);
        showLandmarksOnMap(mapRef, loadObjectsLandmarksOnMap, mapSettingsPreferences, DEFAULT_SLIDE_VALUE);
        showGeofencesOnMap(mapSettingsPreferences, DEFAULT_SLIDE_VALUE, mapRef, loadObjectsGeofenceOnMap);
        showUnitLabels({ ...mapSettingsPreferences, unitLabelSelect: unitLabelSelectForMap }, mapRef);
        showPreventOverlapLabels(mapSettingsPreferences, mapRef);
        setPreferencesLoaded(true);
    };

    useEffect(() => {
        if (preferencesLoaded && mapRef?.current?.showUnitsByPreferences) {
            mapRef.current.showUnitsByPreferences();
            setPreferencesLoaded(false);
        }
    }, [preferencesLoaded, mapRef]);

    const showLabels = mapSettingsVars['unitLabelShow'];

    return (
        <div className="map-settings-panel">
            {/* Units */}
            <section className="map-settings-section">
                <h3 className="map-settings-section-title">{messages['realtimemaps_map_settings_unit']}</h3>
                <div className="map-settings-row">
                    <span className="map-settings-row-label">{messages['realtimemaps_map_settings_unitClusters']}</span>
                    <MapSettingsButton
                        id="unitCluster"
                        mediuml={isSpanish}
                        checked={mapSettingsVars['unitCluster'] || false}
                        onChange={onOptionChange}
                    />
                </div>
                <h4 className="map-settings-subsection-title">{messages['realtimemaps_map_settings_unitLabels']}</h4>
                <div className="map-settings-row">
                    <span className="map-settings-row-label">{messages['realtimemaps_map_settings_pill_label']}</span>
                    <MapSettingsButton
                        id="labelSelect_unit"
                        mediuml={isSpanish}
                        checked={mapSettingsVars['labelSelect_unit'] || false}
                        onChange={onOptionChange}
                    />
                </div>
                <div className="map-settings-row">
                    <span className="map-settings-row-label">{messages['realtimemaps_map_settings_pill_driver']}</span>
                    <MapSettingsButton
                        id="labelSelect_driver"
                        mediuml={isSpanish}
                        checked={mapSettingsVars['labelSelect_driver'] || false}
                        onChange={onOptionChange}
                    />
                </div>
                {showLabels && (
                    <div className="map-settings-row">
                        <span className="map-settings-row-label">{messages['mapsettings_unitlabelsOverlap']}</span>
                        <MapSettingsButton
                            id="unitLabelShowOverlap"
                            mediuml={isSpanish}
                            checked={mapSettingsVars['unitLabelShowOverlap'] || false}
                            onChange={onOptionChange}
                        />
                    </div>
                )}
                {showLabels && (
                    <div className="map-settings-row">
                        <span className="map-settings-row-label">{messages['realtimemaps_map_settings_dark_labels']}</span>
                        <MapSettingsButton
                            id="darkLabels"
                            mediuml={isSpanish}
                            checked={mapSettingsVars['darkLabels'] || false}
                            onChange={onOptionChange}
                        />
                    </div>
                )}
            </section>

            {/* Landmarks */}
            <section className="map-settings-section">
                <h3 className="map-settings-section-title">{messages['mapsettings_landmarks']}</h3>
                <div className="map-settings-row">
                    <span className="map-settings-row-label">{messages['mapsettings_landmarks']}</span>
                    <MapSettingsButton
                        id="landmarkShow"
                        mediuml={isSpanish}
                        checked={mapSettingsVars['landmarkShow'] || false}
                        onChange={onOptionChange}
                    />
                </div>
                <div className="map-settings-slider-group">
                    <span className="map-settings-slider-label">{messages['mapsettings_landmarkmarkersvisibleatzoom']}</span>
                    <SliderRealtimeMaps
                        value={mapSettingsVars['zoomLandmarkMarkersVisible'] || DEFAULT_SLIDE_VALUE}
                        onChange={handleLandmarkMarkersSlider}
                    />
                </div>
                <div className="map-settings-slider-group">
                    <span className="map-settings-slider-label">{messages['mapsettings_landmarkradiusvisibleatzoom']}</span>
                    <SliderRealtimeMaps
                        value={mapSettingsVars['zoomLandmarkRadiusVisibleZoom'] || DEFAULT_SLIDE_VALUE}
                        onChange={handleLandmarkRadiusSlider}
                    />
                </div>
            </section>

            {/* Geofences */}
            <section className="map-settings-section">
                <h3 className="map-settings-section-title">{messages['realtimemaps_geofence']}</h3>
                <div className="map-settings-row">
                    <span className="map-settings-row-label">{messages['realtimemaps_geofence']}</span>
                    <MapSettingsButton
                        id="geofenceShow"
                        mediuml={isSpanish}
                        checked={mapSettingsVars['geofenceShow'] || false}
                        onChange={onOptionChange}
                    />
                </div>
                <div className="map-settings-slider-group">
                    <span className="map-settings-slider-label">{messages['mapsettings_geofencesvisibleatzoom']}</span>
                    <SliderRealtimeMaps
                        value={mapSettingsVars['zoomGeofenceVisible'] || DEFAULT_SLIDE_VALUE}
                        onChange={handleGeofenceSlider}
                    />
                </div>
            </section>

            {/* Current Zoom */}
            {mapRef?.current && (
                <div className="map-settings-zoom-info">
                    {`${messages['realtimemaps_map_settings_current_zoom']}: ${zoom || ''}`}
                </div>
            )}

            {/* Animation */}
            {HasPermission(user, 'animationmap', [281]) && (
                <section className="map-settings-section">
                    <h3 className="map-settings-section-title">{messages['mapsettings_animationMap']}</h3>
                    <div className="map-settings-row">
                        <MapSettingsButton
                            id="animationmap"
                            xxLarge
                            checked={mapSettingsVars['animationmap'] ?? true}
                            optionLabels={[messages['mapsettings_animationMap_activated'], messages['mapsettings_animationMap_deactivated']]}
                            onChange={onOptionChange}
                        />
                    </div>
                </section>
            )}

            {/* Hardware Acceleration (WebGL).  Hidden when the browser cannot use WebGL. */}
            {webglSupported && (
                <section className="map-settings-section">
                    <h3 className="map-settings-section-title">{messages['mapsettings_hardware_acceleration']}</h3>
                    <div className="map-settings-row">
                        <MapSettingsButton
                            id="hardwareAcceleration"
                            xxLarge
                            checked={mapSettingsVars['hardwareAcceleration'] ?? true}
                            optionLabels={[messages['mapsettings_hardware_acceleration_activated'], messages['mapsettings_hardware_acceleration_deactivated']]}
                            onChange={onOptionChange}
                        />
                    </div>
                    <p className="map-settings-help-text">{messages['mapsettings_hardware_acceleration_help']}</p>
                </section>
            )}
        </div>
    );
};

export default MapSettingPreference;